/**
 * Working out which kind of soil recommendation a Soil Type option means.
 *
 * The Soil Type dropdown is filled from `rec_man_types`, filtered to the Soil pin
 * type, so its contents are data rather than code. The ids are assigned by hand in
 * this estate and are not guaranteed to match between environments, so nothing may
 * hardcode one.
 *
 * That leaves the description as the only stable handle, which is why this exists:
 * the matching happens **once**, here, and everything downstream branches on the
 * resolved kind. The component previously did `name.toLowerCase().includes("maintenance")`
 * inline at the point of use, which was already fragile with two options.
 *
 * Changing a description in the database changes behaviour. That is called out in
 * the SQL that creates the SCN row.
 */

export type SoilRecommendationKind = 'preparation' | 'maintenance' | 'scn' | 'unknown';

export interface SoilTypeOption {
  id: number;
  name: string;
}

/**
 * The kind an option represents, from its description.
 *
 * Matching is case-insensitive and on a distinctive substring rather than the whole
 * string, because the descriptions carry small variations between environments and
 * nobody is going to keep them identical by hand.
 *
 * "crop nutrition" rather than "scn": the description the dropdown shows is written
 * for a person, and the SQL that creates it uses the long form.
 */
export function soilRecommendationKind(option: SoilTypeOption | null | undefined): SoilRecommendationKind {
  const name = (option?.name ?? '').toLowerCase();

  if (!name.trim()) {
    return 'unknown';
  }
  // Checked before preparation: nothing currently collides, but "crop nutrition" is
  // the most specific of the three and should win if a description ever carries two.
  if (name.includes('crop nutrition') || name.includes('scn')) {
    return 'scn';
  }
  if (name.includes('maintenance')) {
    return 'maintenance';
  }
  if (name.includes('preparation')) {
    return 'preparation';
  }
  return 'unknown';
}

/** The Analysis Type id that means Soil. */
export const SOIL_ANALYSIS_TYPE = 2;

/**
 * The kind, taking the analysis type into account as well.
 *
 * The Soil Type dropdown is only rendered under Soil, but the id it last held is
 * not cleared by the dropdown disappearing. So after choosing Soil, then Soil Crop
 * Nutrition, then switching to Soft Tissue, the stale id kept resolving to SCN:
 * the SCN steps stayed on screen and the lab submission steps stayed hidden behind
 * them, with no way back short of reloading.
 *
 * Returning unknown for any other analysis type makes that impossible regardless of
 * what the id still holds.
 */
export function soilKindFor(
  analysisTypeId: number | null | undefined,
  options: SoilTypeOption[] | null | undefined,
  selectedSoilTypeId: number | null | undefined,
): SoilRecommendationKind {
  if (analysisTypeId !== SOIL_ANALYSIS_TYPE) {
    return 'unknown';
  }
  return kindOfSelected(options, selectedSoilTypeId);
}

/** The kind for a chosen id, given the loaded options. */
export function kindOfSelected(
  options: SoilTypeOption[] | null | undefined,
  selectedId: number | null | undefined,
): SoilRecommendationKind {
  if (selectedId == null) {
    return 'unknown';
  }
  return soilRecommendationKind((options ?? []).find(o => o.id === selectedId));
}

/**
 * Whether this kind collects a lab submission and samples.
 *
 * SCN does not: it is built from fields and seasons, so the Lab Submission and
 * Samples steps are hidden for it. An unknown kind keeps the existing steps rather
 * than hiding them, so a description nobody anticipated degrades to the old
 * behaviour instead of showing a blank page.
 */
export function usesLabSubmission(kind: SoilRecommendationKind): boolean {
  return kind !== 'scn';
}

/**
 * Whether this kind needs the sample linked to a crop definition.
 *
 * Soil Preparation does not. It interprets an analysis without reference to a crop:
 * SoilPreparationService only copies the link into a display DTO and never computes
 * with it, and its own notes logic records that cultivars "mean nothing on a soil
 * prep report". So Preparation neither asks for the link nor blocks on it.
 *
 * Maintenance DOES need it, and that is why this is not simply "soil does not care".
 * SoilMaintenanceService.cultivarSizeFor reads the planted area through the link and
 * falls back to the whole field without it, so an unlinked sample there does not
 * fail, it silently changes the area every per-block and product total is based on.
 *
 * SCN needs it too, though it degrades rather than breaks: with nothing linked on a
 * field it drops the per-cultivar filter and uses the field's soil values whole.
 */
export function needsCropDefinition(kind: SoilRecommendationKind): boolean {
  return kind !== 'preparation';
}

/** Where a created recommendation of this kind is opened. */
export function routeForKind(kind: SoilRecommendationKind): string {
  switch (kind) {
    case 'scn':
      return 'soil-crop-nutrition';
    case 'maintenance':
      return 'soil-maintenance';
    default:
      // Preparation, and anything unrecognised, which is what this did before.
      return 'soil-preparation';
  }
}

/** Maintenance alone asks for a starting year before it is created. */
export function needsStartingYear(kind: SoilRecommendationKind): boolean {
  return kind === 'maintenance';
}
